import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Table, message, Checkbox, Button, Typography,
} from 'antd';
import { toast } from 'react-toastify';
import { getMainColumns, getExpandedRow } from './TableConfigNew';
import Preloader from '../../../../../components/preloader/Preloader';
import { PreloaderBox } from '../../../../../../../common/ui/Common';

const PAGE_SIZE = 50;

function DetailedReportNew({ settings, preloader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWeight, setShowWeight] = useState(false);
  const [questionListData, setQuestionListData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  const [initialized, setInitialized] = useState(false);
  const { startDate, endDate } = settings;

  const fetchReportData = useCallback(async (page = 1, pageSize = PAGE_SIZE) => {
    setLoading(true);
    preloader();
    const token = window.localStorage.getItem('token');
    const offset = (page - 1) * pageSize;

    try {
      const res = await axios.get('/api/reports/qualityDetailedReport', {
        params: {
          token,
          startDate,
          endDate,
          limit: pageSize,
          offset,
        },
      });

      const { data: responseData } = res;

      if (!responseData.success) {
        message.error(responseData.error || 'Ошибка загрузки отчёта');
        return;
      }

      const { data: reportItems, totalCount, questionList } = responseData;
      const qListData = questionList?.data || [];
      setQuestionListData(qListData);
      setItems(reportItems || []);
      setPagination({ current: page, pageSize, total: totalCount || 0 });
      setInitialized(true);
    } catch (err) {
      console.error('DetailedReportNew fetch error:', err);
      message.error('Ошибка при загрузке детализированного отчёта');
    } finally {
      preloader();
      setLoading(false);
    }
  }, [startDate, endDate, preloader]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchReportData(1, pagination.pageSize);
  }, [settings]);

  const handleTableChange = (pag) => {
    fetchReportData(pag.current, pag.pageSize);
  };

  if (!initialized) {
    return (
      <PreloaderBox>
        <Preloader isOpen small />
      </PreloaderBox>
    );
  }

  const columns = getMainColumns();

  const runExport = async () => {
    const token = window.localStorage.getItem('token');

    let allItems;
    try {
      const res = await axios.get('/api/reports/qualityDetailedReport', {
        params: { token, startDate, endDate },
      });
      if (!res.data?.success) {
        toast.error(res.data?.error || 'Ошибка при подготовке экспорта', 1000);
        return;
      }
      allItems = res.data.data || [];
    } catch (err) {
      toast.error('Ошибка при подготовке экспорта', 1000);
      return;
    }

    const exportColumns = [
      { dataIndex: 'created_on', title: 'Дата оценки' },
      { dataIndex: 'number', title: 'Номер талона' },
      { dataIndex: 'fio', title: 'ФИО сотрудника' },
      { dataIndex: 'avg_score', title: 'Средняя оценка' },
      ...questionListData.map((q) => ({ dataIndex: `q_${q[0]}`, title: q[1] })),
    ];

    const exportItems = allItems.map((item) => {
      const row = {
        created_on: item.created_on,
        number: item.number,
        fio: item.fio,
        avg_score: item.answerdata?.qround || '0.00',
      };
      questionListData.forEach((q) => {
        const answer = item.answerdata?.[q[0]];
        if (answer && typeof answer === 'object') {
          const textVal = answer.answertxt || answer.answerint;
          row[`q_${q[0]}`] = showWeight && textVal
            ? `${textVal} (${answer.weight})`
            : (textVal || answer.weight || '');
        } else {
          row[`q_${q[0]}`] = '';
        }
      });
      return row;
    });

    try {
      const answer = await axios({
        method: 'post',
        url: `/api/reports/?token=${token}`,
        data: {
          type: 'detailedReport',
          token,
          filters: { main: { startDate, endDate } },
          items: exportItems,
          TableConfig: exportColumns,
          needToExport: true,
        },
      });
      if (answer.statusText === 'OK') {
        toast.success('Отчет подготавливается для скачивания и появится в разделе "Архив"', 1000);
      }
    } catch (err) {
      toast.error('Ошибка при экспорте', 1000);
    }
  };

  return (
    <>
      <Typography.Title level={2} style={{ textAlign: 'center', color: '#25547A' }}>
        Детализированный отчет (новый вид)
      </Typography.Title>
      <div style={{
        marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center',
      }}
      >
        <Button type="primary" size="large" onClick={runExport}>
          Экспортировать
        </Button>
        <Checkbox
          checked={showWeight}
          onChange={(e) => setShowWeight(e.target.checked)}
        >
          Показывать числовой эквивалент
        </Checkbox>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        loading={loading}
        expandable={{
          expandedRowRender: getExpandedRow(questionListData, showWeight),
          rowExpandable: (record) => !!record.answerdata,
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          showTotal: (total) => `Всего: ${total}`,
        }}
        onChange={handleTableChange}
        rowKey={(record, index) => record.id || record.sessionquestion || index}
        scroll={{ x: 750 }}
      />
    </>
  );
}

export default DetailedReportNew;
